import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Offre complète";
const h1Text = "Votre partenaire stratégique et opérationnel pour un forfait full-service complet dans la région DACH. En tant que bras prolongé, nous gérons l'intégralité de vos activités locales afin que vous puissiez rester concentré sur vos marchés d'origine.";
const heroImage = "https://net-basis.net/Bilder/ict-full-service-business-development-management.webp";
const heroAlt = "Un homme d'affaires utilise un tableau de bord pour une offre TIC complète à gestion centralisée.";
const heroClaimLine1 = "Tout d'une";
const heroClaimLine2 = "seule source !";
const benefitsTitle = "Comment vous profitez de notre expertise en __offre complète__ :";

const p1Text = "NetBasis International agit pour les entreprises étrangères comme un pilier local sur leurs nouveaux marchés. À partir d'un large portefeuille de prestations, nos partenaires constructeurs peuvent sélectionner des services individuels adaptés à leurs exigences spécifiques.";
const p2Text = "L'approche la plus complète pour pénétrer un nouveau marché, et donc le meilleur moyen de garantir un travail fructueux, reste néanmoins notre formule full-service. De cette manière, nos partenaires constructeurs gardent l'esprit libre pour leurs marchés nationaux et confient, bien entendu en étroite coopération, la gestion complète des nouveaux marchés à NetBasis International.";
const p3Text = "Avec un contrat full-service, NetBasis International prend en charge l'ensemble des activités locales ainsi que la planification, l'exécution et le suivi de toutes les tâches à accomplir.";

const p1Parts = p1Text.split(COMPANY_NAME);
const p2Parts = p2Text.split(COMPANY_NAME);
const p3Parts = p3Text.split(COMPANY_NAME);

const sections = [
  {
    title: "1. Tout inclus",
    items: [
      "Adresse postale et service de bureau complet sur place",
      "Évaluation technique et commerciale de vos produits",
      "Création de tous les documents commerciaux et techniques nécessaires",
      "Analyse concurrentielle intensive et approfondie sur le marché cible",
      "Tarification stratégique et positionnement sur le marché",
      "Commercialisation de produits et solutions sélectionnés ou de l'intégralité en tant qu'importateur",
      "Enregistrement ou notification officielle des produits matériels auprès des autorités (telles que la Stiftung EAR)",
      "Support technique fiable selon des niveaux de service clairement définis",
      "L'équipe de NetBasis International développe votre activité en tant que représentation locale",
    ],
  },
  {
    title: "2. Pragmatique",
    items: [
      "Un interlocuteur central pour vos besoins ainsi que pour vos clients locaux en anglais et en allemand",
      "Vente complète, marketing ciblé et support en votre nom directement sur le marché",
    ],
  },
  {
    title: "3. Rapide",
    items: [
      "Pas de longue phase de démarrage – avec nous, vous êtes immédiatement opérationnel et \"ready for action\"",
      "Concentration directe sur le travail réel et productif sur le terrain",
      "Aucun retard pour contacter les clients potentiels ou les partenaires de distribution qualifiés",
      "Suivi client local simple, visites clients et participation professionnelle aux salons",
    ],
  },
  {
    title: "4. Proche du client",
    items: [
      "Présent sur place sans barrières linguistiques ou culturelles",
      "Image publique professionnelle, expérimentée, fiable et orientée client",
      "Forte agilité entrepreneuriale et délais de réaction extrêmement courts",
    ],
  },
  {
    title: "5. Économique",
    items: [
      "Aucun coût pour un bureau international onéreux et du personnel – coûts fixes minimaux sans investissements lourds",
      "Tarifs forfaitaires mensuels clairs et transparents plus commission sur les résultats selon l'accord de partenariat",
    ],
  },
];

const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

export default function OffreCompleteFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Offre Complète | NetBasis International"
        description="De l'analyse de marché aux ventes : Notre forfait modulaire complet pour le succès clé en main de votre implantation internationale."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/offre-complete"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-end pt-[12%] md:pt-[4%] lg:pt-[4%] pr-[8%] md:pr-[4%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5] animate-[presenceTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="hidden md:inline"> </span>}
                  {heroClaimLine2 && <span className="block md:inline">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Sections with green sub-headings and bullet lists */}
            <div className="space-y-5 text-foreground-950 leading-relaxed text-sm md:text-base">
              {sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-primary-600 font-bold text-sm md:text-base mb-2">
                    {section.title}
                  </h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {section.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}